/**
 * VerificationFeePanel — Client-facing commercial approval UI
 * Shows pending verification engagements for an assessment.
 * SME approves/declines the quoted fee to authorize verification.
 */
import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { Shield, Check, X, RefreshCw, ChevronRight, Info } from "lucide-react";

const STATUS_CONFIG = {
  quote_pending:            { color: "bg-slate-100", text: "text-slate-500",   label: "Quote Pending",              cta: null },
  quote_entered:            { color: "bg-blue-50",   text: "text-blue-700",    label: "Quote Received",             cta: "approve" },
  awaiting_client_approval: { color: "bg-amber-50",  text: "text-amber-700",   label: "Awaiting Your Approval",     cta: "approve" },
  approved_to_proceed:      { color: "bg-emerald-50",text: "text-emerald-700", label: "Approved — In Progress",     cta: null },
  on_hold:                  { color: "bg-red-50",    text: "text-red-700",     label: "On Hold",                    cta: null },
  in_verification:          { color: "bg-indigo-50", text: "text-indigo-700",  label: "Under Verification",         cta: null },
  completed:                { color: "bg-emerald-50",text: "text-emerald-700", label: "Verification Complete",      cta: null },
};

export default function VerificationFeePanel({ assessmentId }) {
  const [engagement, setEngagement] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [acting,     setActing]     = useState(false);
  const [showDetails,setDetails]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/verifier-engagements/assessment/${assessmentId}`);
      setEngagement(res.data);
    } catch {}
    setLoading(false);
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    setActing(true);
    try {
      await client.post(`/verifier-engagements/${engagement.engagement_id}/approve`, {});
      toast.success("Verification approved. The verifier will begin the review.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to approve"); }
    setActing(false);
  };

  const handleDecline = async () => {
    setActing(true);
    try {
      await client.post(`/verifier-engagements/${engagement.engagement_id}/decline`, {});
      toast.info("Engagement placed on hold.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setActing(false);
  };

  if (loading) return null;
  if (!engagement) return null;

  const cfg = STATUS_CONFIG[engagement.commercial_status] || STATUS_CONFIG.quote_pending;
  const needsAction = cfg.cta === "approve";

  return (
    <div className={`rounded-xl border p-4 ${needsAction ? "border-amber-300 bg-amber-50" : `border-slate-200 ${cfg.color}`}`}
      data-testid="verification-fee-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${needsAction ? "bg-amber-100" : "bg-white/50"}`}>
            <Shield size={14} className={needsAction ? "text-amber-600" : "text-indigo-500"} />
          </div>
          <div>
            <p className="text-sm font-bold text-[#0F172A]">Third-Party Verification</p>
            <p className={`text-xs font-semibold mt-0.5 ${cfg.text}`}>{cfg.label}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Verifier: <strong>{engagement.verifier_org_name}</strong>
            </p>
          </div>
        </div>
        {engagement.quoted_fee && (
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-[#0F172A] tabular-nums">
              {engagement.quoted_fee.toLocaleString()} <span className="text-sm font-normal text-slate-400">{engagement.currency}</span>
            </p>
            <p className="text-[10px] text-slate-400">Quoted Fee</p>
          </div>
        )}
      </div>

      {engagement.fee_notes && (
        <div className="mt-3 px-3 py-2 bg-white/60 rounded-lg text-xs text-slate-600">
          {engagement.fee_notes}
        </div>
      )}

      {/* Approval CTAs */}
      {needsAction && (
        <div className="mt-4 flex gap-2">
          <button onClick={handleApprove} disabled={acting} data-testid="approve-engagement-btn"
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50">
            {acting ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
            Approve & Proceed
          </button>
          <button onClick={handleDecline} disabled={acting} data-testid="decline-engagement-btn"
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 border border-slate-300 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 disabled:opacity-50">
            <X size={13} /> Decline
          </button>
        </div>
      )}

      {/* Show payment status if post-approval */}
      {["approved_to_proceed","in_verification","completed"].includes(engagement.commercial_status) && (
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <span>Payment: <strong className="text-slate-700">{engagement.payment_status?.replace(/_/g," ")}</strong></span>
          {engagement.client_approved_at && <span>Approved: {engagement.client_approved_at?.slice(0,10)}</span>}
        </div>
      )}

      {/* Details toggle */}
      <button onClick={() => setDetails(!showDetails)}
        className="mt-2 flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
        <ChevronRight size={11} className={`transition-transform ${showDetails?"rotate-90":""}`} />
        {showDetails?"Hide":"Show"} engagement details
      </button>
      {showDetails && (
        <div className="mt-2 space-y-1 text-xs text-slate-500 bg-white/60 rounded-lg p-3">
          <p>Engagement ID: <span className="font-mono font-medium text-slate-700">{engagement.engagement_id}</span></p>
          <p>Commercial status: <span className="capitalize">{engagement.commercial_status?.replace(/_/g," ")}</span></p>
          <p>Created: {engagement.created_at?.slice(0,10)}</p>
        </div>
      )}
    </div>
  );
}
